/**
 * ClientProfileCard — Perfil individualizado do cliente com sugestões proativas.
 *
 * Exibido ao selecionar um cliente no modal "Novo agendamento".
 * Mostra: profissional favorito, serviço mais frequente, dia+hora preferidos,
 * ciclo de retorno e 2-3 slots sugeridos que combinam com o perfil.
 *
 * Ao clicar num slot, o formulário do agendamento é preenchido automaticamente.
 */
import { useEffect, useState } from "react";
import { Sparkles, User, Wrench, Calendar as CalendarIcon, Clock, Repeat, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const formatDateBR = (iso) => {
  try {
    const dt = new Date(iso);
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", weekday: "short" });
  } catch { return iso; }
};
const formatTimeBR = (iso) => {
  try {
    const dt = new Date(iso);
    return dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function ClientProfileCard({ clientId, onPickSlot }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) { setProfile(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/tenant/clients/${clientId}/profile`)
      .then(({ data }) => { if (!cancelled) setProfile(data); })
      .catch(() => { if (!cancelled) setProfile(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (!clientId) return null;
  if (loading) {
    return (
      <Card className="p-3 bg-muted/30 border-dashed" data-testid="client-profile-loading">
        <div className="text-xs text-muted-foreground animate-pulse">Analisando perfil deste cliente...</div>
      </Card>
    );
  }
  if (!profile) return null;

  const isNew = (profile.total_appointments || 0) === 0;

  return (
    <Card className="p-3 space-y-2.5 border-tenant/40 bg-tenant/5" data-testid="client-profile-card">
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-tenant" />
        <div className="text-xs font-semibold uppercase tracking-wider text-tenant">
          Perfil deste cliente
        </div>
        {isNew && (
          <Badge variant="outline" className="text-[10px] ml-auto">Cliente novo</Badge>
        )}
      </div>

      {isNew ? (
        <div className="text-xs text-muted-foreground">
          Sem histórico ainda. Sugestões abaixo usam a próxima janela livre.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="client-profile-chips">
          {profile.favorite_professional?.name && (
            <Badge variant="outline" className="text-[11px] gap-1">
              <User className="w-3 h-3" /> {profile.favorite_professional.name}
            </Badge>
          )}
          {profile.favorite_service?.name && (
            <Badge variant="outline" className="text-[11px] gap-1">
              <Wrench className="w-3 h-3" /> {profile.favorite_service.name}
            </Badge>
          )}
          {profile.favorite_weekday?.label && (
            <Badge variant="outline" className="text-[11px] gap-1">
              <CalendarIcon className="w-3 h-3" /> {profile.favorite_weekday.label}
            </Badge>
          )}
          {profile.favorite_hour?.label && (
            <Badge variant="outline" className="text-[11px] gap-1">
              <Clock className="w-3 h-3" /> {profile.favorite_hour.label}
            </Badge>
          )}
          {profile.avg_return_days && (
            <Badge variant="outline" className="text-[11px] gap-1">
              <Repeat className="w-3 h-3" /> {profile.avg_return_days}d entre visitas
            </Badge>
          )}
        </div>
      )}

      {profile.suggested_slots && profile.suggested_slots.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            Sugestões de horário
          </div>
          <div className="grid grid-cols-1 gap-1.5" data-testid="client-profile-suggestions">
            {profile.suggested_slots.map((s, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onPickSlot?.(s)}
                data-testid={`client-profile-slot-${idx}`}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-tenant/10 hover:border-tenant/50 transition-colors text-left"
              >
                <CalendarIcon className="w-3.5 h-3.5 text-tenant shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium">
                    {formatDateBR(s.start_at)} — {formatTimeBR(s.start_at)}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {s.service_name}
                    {s.professional_name ? ` · ${s.professional_name}` : ""}
                    {(s.matches_favorite_weekday && s.matches_favorite_hour)
                      ? " · dia + hora preferidos"
                      : s.matches_favorite_weekday ? " · dia preferido" : ""}
                  </div>
                </div>
                <ArrowRight className="w-3 h-3 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
